var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { IsBoolean, IsEnum, IsISO8601, IsOptional } from 'class-validator';
import { InvoiceRunScopeMode } from '../enums/invoice-run-scope-mode.enum.js';
/**
 * Body of `POST /v1/office-locations/:officeLocationId/invoice-run` — kicks
 * off a new `InvoiceRun` for the given OfficeLocation.
 *
 * Slice 3 adds the off-by-default `alsoIssue` config knob: when set, each
 * successfully assembled draft is also issued (assign number → render PDF →
 * archive). If issuing fails, the invoice falls back to a usable draft and the
 * failure reason is recorded on the item (`issueFailureMessage`).
 *
 * Slice 4 adds the candidate-selection knobs: `deliveredUpTo` (cutoff for
 * which delivered units are eligible — default: no cutoff = today) and
 * `consolidate` (run-level toggle that merges each patient's compatible
 * plans into one invoice). The remaining `scopeMode` knob layers on in
 * slice 5: `scopeMode` (`all_delivered` default | `completed_only` — narrows
 * the run to plans with `status == COMPLETED`, release-coupled to TM-9102).
 * `officeLocationId` is supplied in the URL.
 */
export class ExecuteInvoiceRunDto {
    /**
     * Default `false` — run creates drafts only. When `true`, the worker also
     * issues each draft; on issue failure the item stays a usable draft with
     * the failure reason recorded.
     */
    alsoIssue;
    /**
     * Slice 4 — ISO-8601 cutoff date. Only units delivered up to and including
     * this date are eligible (`unit.delivered_at <= deliveredUpTo`). Omitting
     * yields the slice-1 behaviour (no cutoff). The UI defaults to today.
     */
    deliveredUpTo;
    /**
     * Slice 4 — run-level consolidation toggle. Default `false`: each patient's
     * units are split per treatment plan (one invoice per plan). When `true`,
     * every patient's units within each compatible context
     * (`patientId × payerDomainCode × currency × officeLocationId`) merge into
     * a single invoice.
     */
    consolidate;
    /**
     * Slice 5 — scope mode for plan eligibility filtering. `all_delivered`
     * (default) keeps slices 1-4 behaviour. `completed_only` narrows to plans
     * with `status == COMPLETED`. Release-coupled to TM-9102.
     */
    scopeMode;
}
__decorate([
    IsOptional(),
    IsBoolean(),
    __metadata("design:type", Boolean)
], ExecuteInvoiceRunDto.prototype, "alsoIssue", void 0);
__decorate([
    IsOptional(),
    IsISO8601(),
    __metadata("design:type", String)
], ExecuteInvoiceRunDto.prototype, "deliveredUpTo", void 0);
__decorate([
    IsOptional(),
    IsBoolean(),
    __metadata("design:type", Boolean)
], ExecuteInvoiceRunDto.prototype, "consolidate", void 0);
__decorate([
    IsOptional(),
    IsEnum(InvoiceRunScopeMode),
    __metadata("design:type", String)
], ExecuteInvoiceRunDto.prototype, "scopeMode", void 0);
/**
 * Response of `POST /v1/office-locations/:officeLocationId/invoice-run`. The
 * frontend uses `invoiceRunId` to start polling the status query and to drive
 * the Running / Completed views.
 */
export class ExecuteInvoiceRunResponseDto {
    invoiceRunId;
}
//# sourceMappingURL=execute-invoice-run.dto.js.map